/**
 * Cart Price Refresh System v2.0
 * 
 * Detects when cart items need updating due to price changes:
 * 1. For B2B customers: Missing B2B properties or outdated wholesale prices
 * 2. For all customers: Variant prices updated (gold price changes daily)
 * 
 * This ensures all customers always see current pricing.
 */

class B2BCartRefresh {
  constructor() {
    this.debugMode = window.Shopify?.theme?.settings?.b2b_debug_mode || false;
    this.isB2B = window.customer?.tags?.includes('b2b') || window.customer?.tags?.includes('wholesale') || false;
    this.customerId = window.customer?.id || null;
    this.refreshReason = null; // 'missing_properties', 'price_changed', or 'variant_price_changed'
  }

  /**
   * Normalize properties - Shopify sometimes returns array of arrays instead of object
   * Converts [["key1","value1"],["key2","value2"]] to {key1: "value1", key2: "value2"}
   */
  normalizeProperties(properties) {
    if (!properties) return {};
    
    // Already an object
    if (typeof properties === 'object' && !Array.isArray(properties)) {
      return properties;
    }
    
    // Array of arrays format: [["key", "value"], ...]
    if (Array.isArray(properties)) {
      const normalized = {};
      properties.forEach(prop => {
        if (Array.isArray(prop) && prop.length >= 2) {
          normalized[prop[0]] = prop[1];
        }
      });
      return normalized;
    }
    
    return {};
  }

  /**
   * Initialize - check if cart needs refresh (for all customers)
   */
  async init() {
    // Check if notification should be shown after reload
    if (sessionStorage.getItem('b2b_cart_refreshed')) {
      sessionStorage.removeItem('b2b_cart_refreshed');
      this.showSuccessNotification();
      return;
    }

    // Check on cart page immediately
    const isCartPage = window.location.pathname.includes('/cart');
    
    if (isCartPage) {
      this.log('On cart page, checking prices');
      await this.checkAndShowBanner();
    } else {
      this.log('Not on cart page, setting up cart drawer listener');
    }
    
    // Listen for cart drawer opening on any page
    this.setupCartDrawerListener();
  }
  
  /**
   * Set up listener for cart drawer opening
   */
  setupCartDrawerListener() {
    // Watch for cart drawer becoming visible
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          // Check if cart drawer is opening (look for common classes)
          if (target.classList.contains('is-open') || 
              target.classList.contains('active') ||
              target.classList.contains('open')) {
            this.log('Cart drawer opened, checking prices');
            this.checkAndShowBannerInDrawer();
          }
        }
      }
    });
    
    // Observe cart drawer elements
    const cartDrawers = document.querySelectorAll('.bls__canvas, .cart-drawer, .mini-cart, [data-cart-drawer]');
    cartDrawers.forEach(drawer => {
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });
    
    // Also listen for cart icon clicks
    document.querySelectorAll('.bls-minicart-action, [data-cart-toggle], .cart-icon').forEach(btn => {
      btn.addEventListener('click', () => {
        setTimeout(() => this.checkAndShowBannerInDrawer(), 500);
      });
    });
  }
  
  /**
   * Check prices and show banner (on cart page)
   */
  async checkAndShowBanner() {
    const needsRefresh = await this.checkCartNeedsRefresh();
    
    if (needsRefresh) {
      this.log(`Cart needs refresh: ${this.refreshReason}`);
      this.showRefreshBanner();
    } else {
      this.log('Cart prices are current');
    }
  }
  
  /**
   * Check prices and show inline notice in cart drawer
   */
  async checkAndShowBannerInDrawer() {
    // Don't check if already checked recently
    if (this.lastDrawerCheck && Date.now() - this.lastDrawerCheck < 5000) {
      return;
    }
    this.lastDrawerCheck = Date.now();
    
    const needsRefresh = await this.checkCartNeedsRefresh();
    
    if (needsRefresh) {
      this.log(`Cart drawer needs refresh: ${this.refreshReason}`);
      this.showDrawerNotice();
    }
  }

  /**
   * Check if any cart items need refresh (works for all customers)
   */
  async checkCartNeedsRefresh() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await response.json();

      if (!cart.items || cart.items.length === 0) {
        this.log('Cart is empty');
        return false;
      }

      // Store cart data for later use
      this.cartItems = cart.items;

      // For B2B customers, check for missing B2B properties
      if (this.isB2B && this.customerId) {
        const itemsMissingProperties = cart.items.filter(item => {
          const props = this.normalizeProperties(item.properties);
          const hasProps = Object.keys(props).length > 0;
          
          if (!hasProps) {
            this.log(`Item ${item.variant_id} has no properties`);
            return true;
          }
          
          const hasB2BType = props['_b2b_customer_type'] === 'wholesale' || props['B2B Customer Type'] === 'wholesale';
          const hasB2BPrice = (props['_b2b_wholesale_price'] != null && props['_b2b_wholesale_price'] !== '') ||
                             (props['B2B Wholesale Price'] != null && props['B2B Wholesale Price'] !== '');
          
          return !hasB2BType || !hasB2BPrice;
        });

        if (itemsMissingProperties.length > 0) {
          this.log('Items missing B2B properties:', itemsMissingProperties.length);
          this.refreshReason = 'missing_properties';
          return true;
        }
      }

      // Fetch current pricing data for all cart items
      const currentPricing = await this.fetchAllPricing(cart.items);
      
      // For B2B customers, check if wholesale prices have changed
      this.log('isB2B:', this.isB2B, 'customerId:', this.customerId);
      
      if (this.isB2B && this.customerId) {
        const itemsWithOutdatedB2BPrices = cart.items.filter(item => {
          const props = this.normalizeProperties(item.properties);
          // Convert variant_id to string for object key lookup
          const variantIdStr = String(item.variant_id);
          const currentVariantPricing = currentPricing[variantIdStr];
          
          this.log(`Checking variant ${variantIdStr}:`, {
            props,
            currentVariantPricing,
            pricingKeys: Object.keys(currentPricing)
          });
          
          if (!currentVariantPricing || !currentVariantPricing.has_b2b_pricing) {
            this.log(`No B2B pricing found for variant ${variantIdStr}`);
            return false;
          }
          
          const storedWholesalePrice = props['_b2b_wholesale_price'] || props['B2B Wholesale Price'];
          const storedOriginalPrice = props['_b2b_original_price'] || props['B2B Original Price'];
          
          this.log(`Stored prices: wholesale=${storedWholesalePrice}, original=${storedOriginalPrice}`);
          
          if (!storedWholesalePrice) {
            this.log('No stored wholesale price, skipping');
            return false;
          }
          
          // Compare wholesale prices
          const storedWholesaleNum = parseInt(storedWholesalePrice, 10);
          const currentWholesaleNum = parseInt(currentVariantPricing.wholesale_price, 10);
          
          this.log(`Comparing: stored=${storedWholesaleNum}, current=${currentWholesaleNum}, different=${storedWholesaleNum !== currentWholesaleNum}`);
          
          if (storedWholesaleNum !== currentWholesaleNum) {
            this.log(`✅ Wholesale price changed for variant ${item.variant_id}: stored=${storedWholesaleNum}, current=${currentWholesaleNum}`);
            return true;
          }
          
          // Also compare original prices
          if (storedOriginalPrice) {
            const storedOriginalNum = parseInt(storedOriginalPrice, 10);
            const currentOriginalNum = parseInt(currentVariantPricing.original_price, 10);
            
            if (storedOriginalNum !== currentOriginalNum) {
              this.log(`✅ Original price changed for variant ${item.variant_id}: stored=${storedOriginalNum}, current=${currentOriginalNum}`);
              return true;
            }
          }
          
          return false;
        });

        if (itemsWithOutdatedB2BPrices.length > 0) {
          this.log('Items with outdated B2B prices:', itemsWithOutdatedB2BPrices.length);
          this.refreshReason = 'price_changed';
          return true;
        }
      }

      // For ALL customers: check if variant prices have changed
      const itemsWithOutdatedVariantPrices = cart.items.filter(item => {
        const currentVariantPricing = currentPricing[item.variant_id];
        
        if (!currentVariantPricing) {
          return false;
        }
        
        // Compare cart item price with current variant price
        // item.price is in cents, currentVariantPricing.variant_price is also in cents
        const cartPrice = item.price; // This is the price when item was added
        const currentPrice = parseInt(currentVariantPricing.variant_price, 10);
        
        if (cartPrice !== currentPrice) {
          this.log(`Variant price changed for ${item.variant_id}: cart=${cartPrice}, current=${currentPrice}`);
          return true;
        }
        
        return false;
      });

      if (itemsWithOutdatedVariantPrices.length > 0) {
        this.log('Items with outdated variant prices:', itemsWithOutdatedVariantPrices.length);
        this.refreshReason = 'variant_price_changed';
        return true;
      }

      this.log('All cart items have current pricing');
      return false;
    } catch (error) {
      this.log('Error checking cart:', error);
      return false;
    }
  }

  /**
   * Show banner prompting user to refresh cart
   */
  showRefreshBanner() {
    // Don't show if already dismissed or recently refreshed
    if (sessionStorage.getItem('cart_banner_dismissed') || sessionStorage.getItem('cart_recently_refreshed')) {
      this.log('Banner dismissed or recently refreshed, not showing');
      return;
    }

    // Different message based on refresh reason
    let message, buttonText;
    
    switch (this.refreshReason) {
      case 'missing_properties':
        message = 'Some items in your cart need B2B pricing applied.';
        buttonText = 'Apply B2B Pricing';
        break;
      case 'price_changed':
        message = 'Prices have been updated. Refresh your cart to see the latest B2B pricing.';
        buttonText = 'Update Prices';
        break;
      case 'variant_price_changed':
        message = 'Product prices have been updated. Refresh your cart to see the latest prices.';
        buttonText = 'Update Prices';
        break;
      default:
        message = 'Your cart needs to be refreshed.';
        buttonText = 'Refresh Cart';
    }

    const banner = document.createElement('div');
    banner.id = 'b2b-refresh-banner';
    banner.innerHTML = `
      <div class="b2b-refresh-banner__content">
        <div class="b2b-refresh-banner__text">
          <p>${message}</p>
        </div>
        <div class="b2b-refresh-banner__actions">
          <button class="b2b-refresh-banner__btn b2b-refresh-banner__btn--primary" id="b2b-refresh-btn">
            ${buttonText}
          </button>
          <button class="b2b-refresh-banner__btn b2b-refresh-banner__btn--secondary" id="b2b-dismiss-btn">
            Dismiss
          </button>
        </div>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      #b2b-refresh-banner {
        position: fixed;
        bottom: 20px;
        left: 20px;
        right: 20px;
        max-width: 420px;
        z-index: 999999;
        animation: b2b-slide-up 360ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      .b2b-refresh-banner__content {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 16px 20px;
        background: #12274f;
        color: #faf9f7;
        box-shadow: 0 4px 24px rgba(18, 39, 79, 0.2);
        font-family: var(--am-font-body, 'Plus Jakarta Sans', sans-serif);
        overflow: hidden;
      }
      .b2b-refresh-banner__content::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(90deg, transparent 0%, #b8944c 50%, transparent 100%);
      }
      .b2b-refresh-banner__text p {
        font-size: 13px;
        margin: 0;
        color: #faf9f7;
        line-height: 1.5;
      }
      .b2b-refresh-banner__actions {
        display: flex;
        gap: 8px;
      }
      .b2b-refresh-banner__btn {
        padding: 9px 16px;
        border: none;
        font-family: var(--am-font-body, 'Plus Jakarta Sans', sans-serif);
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        cursor: pointer;
        transition: background 200ms ease, color 200ms ease;
      }
      .b2b-refresh-banner__btn--primary {
        background: #b8944c;
        color: #ffffff;
      }
      .b2b-refresh-banner__btn--primary:hover {
        background: #cca65e;
      }
      .b2b-refresh-banner__btn--primary:disabled {
        background: rgba(250, 249, 247, 0.15);
        color: rgba(250, 249, 247, 0.4);
        cursor: not-allowed;
      }
      .b2b-refresh-banner__btn--secondary {
        background: transparent;
        color: rgba(250, 249, 247, 0.7);
        border: 1px solid rgba(250, 249, 247, 0.15);
      }
      .b2b-refresh-banner__btn--secondary:hover {
        color: #faf9f7;
        border-color: rgba(250, 249, 247, 0.3);
      }
      @keyframes b2b-slide-up {
        from { transform: translateY(100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      @keyframes b2b-slide-down {
        from { transform: translateY(0); opacity: 1; }
        to { transform: translateY(100%); opacity: 0; }
      }
      @media (max-width: 600px) {
        #b2b-refresh-banner {
          left: 12px;
          right: 12px;
          bottom: 12px;
        }
        .b2b-refresh-banner__actions {
          flex-direction: column;
        }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(banner);

    // Button handlers
    document.getElementById('b2b-refresh-btn').addEventListener('click', () => {
      this.refreshCart();
    });

    document.getElementById('b2b-dismiss-btn').addEventListener('click', () => {
      sessionStorage.setItem('cart_banner_dismissed', 'true');
      this.hideBanner(banner);
    });
  }

  /**
   * Hide the banner with animation
   */
  hideBanner(banner) {
    banner.style.animation = 'b2b-slide-down 0.3s ease-in forwards';
    setTimeout(() => banner.remove(), 300);
  }

  /**
   * Show notice in cart drawer (more subtle than full page banner)
   */
  showDrawerNotice() {
    // Don't show if already dismissed or recently refreshed
    if (sessionStorage.getItem('cart_banner_dismissed') || sessionStorage.getItem('cart_recently_refreshed')) {
      return;
    }
    
    // Remove existing notice if any
    const existing = document.querySelector('.b2b-drawer-notice');
    if (existing) existing.remove();
    
    // Find the cart drawer subtotal area
    const drawerSubtotal = document.querySelector('.bls-minicart-content .bls-minicart-total, .bls-minicart-content .cart-subtotal, .mini-cart .cart-total');
    if (!drawerSubtotal) {
      this.log('Could not find drawer subtotal area');
      return;
    }
    
    const notice = document.createElement('div');
    notice.className = 'b2b-drawer-notice';
    notice.innerHTML = `
      <div class="b2b-drawer-notice__content">
        <span class="b2b-drawer-notice__text">Prices updated</span>
        <button class="b2b-drawer-notice__btn" id="b2b-drawer-refresh-btn">Refresh</button>
      </div>
    `;
    
    const style = document.createElement('style');
    style.id = 'b2b-drawer-notice-style';
    if (!document.getElementById('b2b-drawer-notice-style')) {
      style.textContent = `
        .b2b-drawer-notice {
          margin: 10px 0;
          padding: 10px 14px;
          background: rgba(184, 148, 76, 0.08);
          border: 1px solid rgba(184, 148, 76, 0.2);
          font-family: var(--am-font-body, 'Plus Jakarta Sans', sans-serif);
          font-size: 12px;
        }
        .b2b-drawer-notice__content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }
        .b2b-drawer-notice__text {
          color: #12274f;
        }
        .b2b-drawer-notice__btn {
          background: #12274f;
          color: #faf9f7;
          border: none;
          padding: 6px 14px;
          font-family: var(--am-font-body, 'Plus Jakarta Sans', sans-serif);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 200ms ease;
        }
        .b2b-drawer-notice__btn:hover {
          background: #1e3a6b;
        }
        .b2b-drawer-notice__btn:disabled {
          background: rgba(18, 39, 79, 0.2);
          cursor: not-allowed;
        }
      `;
      document.head.appendChild(style);
    }
    
    drawerSubtotal.parentNode.insertBefore(notice, drawerSubtotal);
    
    document.getElementById('b2b-drawer-refresh-btn').addEventListener('click', () => {
      this.refreshCart();
    });
  }

  /**
   * Refresh cart - clear and re-add items with current pricing
   * Works for both B2B and regular customers
   */
  async refreshCart() {
    const btn = document.getElementById('b2b-refresh-btn') || document.getElementById('b2b-drawer-refresh-btn');
    const originalText = btn?.textContent || 'Refresh';
    
    try {
      if (btn) {
        btn.textContent = 'Refreshing...';
        btn.disabled = true;
      }

      // Get current cart
      const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartResponse.json();
      const preservedCartState = this.captureCartState(cart);

      if (!cart.items || cart.items.length === 0) {
        this.log('Cart is empty');
        return;
      }

      // Get current pricing for all products (includes B2B pricing if applicable)
      const currentPricing = await this.fetchAllPricing(cart.items);
      this.log('Current Pricing:', currentPricing);

      // Prepare items to re-add
      const itemsToReAdd = cart.items.map(item => {
        const variantPricing = currentPricing[item.variant_id];
        const properties = this.filterNonB2BProperties(this.normalizeProperties(item.properties));
        
        // Add B2B properties if customer is B2B and has pricing (underscore prefix hides them in checkout)
        if (this.isB2B && variantPricing?.has_b2b_pricing) {
          properties['_b2b_customer_type'] = 'wholesale';
          properties['_b2b_wholesale_price'] = variantPricing.wholesale_price;
          properties['_b2b_original_price'] = variantPricing.original_price;
        }
        
        return {
          id: item.variant_id,
          quantity: item.quantity,
          properties: properties
        };
      });

      this.log('Items to re-add:', itemsToReAdd);

      // Clear cart
      await fetch('/cart/clear.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
      });

      // Re-add items with B2B properties
      for (const item of itemsToReAdd) {
        await this.addToCart(item);
        await this.delay(100);
      }

      await this.restoreCartState(preservedCartState);

      // Set flags for success message and to prevent banner from showing again
      sessionStorage.setItem('b2b_cart_refreshed', 'true');
      sessionStorage.setItem('b2b_refresh_reason', this.refreshReason || 'updated');
      sessionStorage.setItem('cart_recently_refreshed', 'true');
      sessionStorage.removeItem('cart_banner_dismissed');

      // Re-apply the last known discount code if the theme stored one locally.
      // Shopify doesn't expose entered cart codes through the Ajax cart in a reliable way.
      if (preservedCartState.discountCode) {
        const currentUrl = `${window.location.pathname}${window.location.search}`;
        window.location.assign(`/discount/${encodeURIComponent(preservedCartState.discountCode)}?redirect=${encodeURIComponent(currentUrl)}`);
        return;
      }

      // Reload page to show updated cart
      window.location.reload();

    } catch (error) {
      console.error('[B2B Cart Refresh] Error:', error);
      if (btn) {
        btn.textContent = 'Error - Try Again';
        btn.disabled = false;
      }
      setTimeout(() => {
        if (btn) {
          btn.textContent = originalText;
        }
      }, 3000);
    }
  }

  /**
   * Capture cart-level state that would otherwise be lost by clear + re-add
   */
  captureCartState(cart) {
    const noteField = document.querySelector('textarea[name="note"], #Cart-note');
    const note = noteField ? noteField.value : (cart.note || '');

    const attributes = { ...(cart.attributes || {}) };
    document.querySelectorAll('[name^="attributes["]').forEach((input) => {
      const match = input.name && input.name.match(/^attributes\[(.+)\]$/);
      if (!match) return;

      const key = match[1];
      if (input.type === 'checkbox') {
        attributes[key] = input.checked ? (input.value || 'Yes') : '';
      } else {
        attributes[key] = input.value;
      }
    });

    return {
      note,
      attributes,
      discountCode: localStorage.getItem('discount_code') || '',
    };
  }

  /**
   * Restore note and cart attributes after rebuilding the cart
   */
  async restoreCartState(cartState) {
    if (!cartState) return;

    const payload = {
      note: cartState.note || '',
      attributes: cartState.attributes || {},
    };

    this.log('Restoring cart state:', payload);

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('Failed to restore cart note/attributes');
    }

    return response.json();
  }

  /**
   * Fetch all pricing for cart items (variant price + B2B pricing if applicable)
   */
  async fetchAllPricing(cartItems) {
    const pricing = {};
    const uniqueHandles = [...new Set(cartItems.map(item => item.handle).filter(Boolean))];

    this.log('Fetching pricing for handles:', uniqueHandles);

    for (const handle of uniqueHandles) {
      try {
        const url = `/products/${handle}?view=b2b-json`;
        
        let response = await fetch(url, {
          credentials: 'same-origin'
        });

        if (response.ok) {
          const text = await response.text();
          
          // Check if response is HTML instead of JSON
          if (text.trim().startsWith('<') || text.trim().startsWith('<!')) {
            this.log(`Received HTML instead of JSON for ${handle}, template may not exist`);
            continue;
          }
          
          try {
            const data = JSON.parse(text);
            
            if (data.variants) {
              Object.entries(data.variants).forEach(([variantId, variantData]) => {
                pricing[variantId] = variantData;
              });
              this.log(`Got pricing for ${handle}:`, Object.keys(data.variants).length, 'variants');
            }
          } catch (e) {
            this.log(`JSON parse error for ${handle}:`, e.message);
          }
        }
        
        await this.delay(50);
      } catch (err) {
        this.log(`Fetch error for ${handle}:`, err.message);
      }
    }

    this.log('Final pricing keys:', Object.keys(pricing));
    
    return pricing;
  }

  /**
   * Alias for backwards compatibility
   */
  async fetchB2BPricing(cartItems) {
    return this.fetchAllPricing(cartItems);
  }

  /**
   * Filter out B2B properties from existing properties
   */
  filterNonB2BProperties(properties) {
    if (!properties) return {};
    
    const filtered = {};
    const b2bKeys = [
      // New underscore-prefixed names (hidden in checkout)
      '_b2b_customer_type', '_b2b_wholesale_price', '_b2b_original_price',
      // Old names (visible in checkout)
      'B2B Customer Type', 'B2B Wholesale Price', 'B2B Original Price',
      // Legacy names
      '_customer_type', '_wholesale_price', '_original_price', '_wholesale_price_cents'
    ];
    
    for (const [key, value] of Object.entries(properties)) {
      if (!b2bKeys.includes(key) && !key.startsWith('B2B') && !key.startsWith('_b2b')) {
        filtered[key] = value;
      }
    }
    
    return filtered;
  }

  /**
   * Add item to cart
   */
  async addToCart(item) {
    const payload = {
      id: item.id,
      quantity: item.quantity
    };

    if (item.properties && Object.keys(item.properties).length > 0) {
      payload.properties = item.properties;
    }

    this.log('Adding to cart:', payload);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('Failed to add item to cart');
    }

    return response.json();
  }

  /**
   * Show success notification after cart refresh
   */
  showSuccessNotification() {
    const refreshReason = sessionStorage.getItem('b2b_refresh_reason');
    sessionStorage.removeItem('b2b_refresh_reason');
    
    let message;
    switch (refreshReason) {
      case 'price_changed':
        message = 'Cart updated with latest B2B pricing!';
        break;
      case 'variant_price_changed':
        message = 'Cart prices updated to latest rates!';
        break;
      case 'missing_properties':
        message = 'B2B pricing applied to your cart!';
        break;
      default:
        message = 'Cart prices updated!';
    }

    const notification = document.createElement('div');
    notification.className = 'b2b-success-notification';
    notification.innerHTML = `
      <div class="b2b-success-notification__bar"></div>
      <div class="b2b-success-notification__content">
        <span class="b2b-success-notification__icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </span>
        <span class="b2b-success-notification__text">${message}</span>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      .b2b-success-notification {
        position: fixed;
        top: 16px;
        right: 16px;
        z-index: 999999;
        animation: b2b-fade-in 360ms cubic-bezier(0.22, 1, 0.36, 1);
      }
      .b2b-success-notification__bar {
        height: 2px;
        background: linear-gradient(90deg, transparent 0%, #b8944c 50%, transparent 100%);
      }
      .b2b-success-notification__content {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 13px 16px;
        background: #12274f;
        color: #faf9f7;
        box-shadow: 0 4px 24px rgba(18, 39, 79, 0.2);
        font-family: var(--am-font-body, 'Plus Jakarta Sans', sans-serif);
        font-size: 13px;
        font-weight: 500;
      }
      .b2b-success-notification__icon {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: rgba(184, 148, 76, 0.15);
        color: #b8944c;
      }
      @keyframes b2b-fade-in {
        from { opacity: 0; transform: translateY(-8px) scale(0.98); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes b2b-fade-out {
        from { opacity: 1; transform: translateY(0) scale(1); }
        to { opacity: 0; transform: translateY(-8px) scale(0.98); }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(notification);

    // Auto-hide after 4 seconds
    setTimeout(() => {
      notification.style.animation = 'b2b-fade-out 0.3s ease-in forwards';
      setTimeout(() => notification.remove(), 300);
    }, 4000);
  }

  /**
   * Utility functions
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  log(...args) {
    if (this.debugMode) {
      console.log('[B2B Cart Refresh]', ...args);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const b2bRefresh = new B2BCartRefresh();
    b2bRefresh.init();
  });
} else {
  const b2bRefresh = new B2BCartRefresh();
  b2bRefresh.init();
}

// Export for manual usage
window.B2BCartRefresh = B2BCartRefresh;
